import { getData, http, sendData } from './http';
import type {
  AdminUser, ApiEnvelope, AppNotification, Attachment, AuditEntry, Comment, CommentVisibility, CustomerTier, Me, NotificationPrefs, Organization,
  Page, Role, SlaPolicy, TenantSettings, Ticket, TicketInput, TicketListFilters, TicketStatus, TimelineEvent, UserPage, UserSummary,
  DashboardSummary, ExportFormat, ReportMeta, ReportResult, ReportSchedule, SavedSearch, SavedSearchFilters, SurveyState,
  Branding, PrivacyRequest, PrivacyStatus, PrivacyType, PermissionCatalog, RoleView, TicketCategory, UploadLimits,
} from '../types/api';

/* ----- downloads ----- */

function fileNameFrom(disposition: unknown, fallback: string): string {
  const match = typeof disposition === 'string' ? /filename="([^"]+)"/.exec(disposition) : null;
  return match?.[1] ?? fallback;
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/** Authenticated download (the bearer token lives in memory). Returns whether the export was truncated. */
export async function downloadExport(path: string, params: Record<string, string>, fallbackName: string): Promise<{ truncated: boolean }> {
  const res = await http.get<Blob>(path, { params, responseType: 'blob', timeout: 2 * 60_000 });
  saveBlob(res.data, fileNameFrom(res.headers['content-disposition'], fallbackName));
  return { truncated: res.headers['x-export-truncated'] === 'true' };
}

/* ----- reports & dashboard ----- */
export interface RangeParams {
  from: string;
  to: string;
  product?: string;
}
const rangeQuery = (r: RangeParams): Record<string, string> => ({ from: r.from, to: r.to, ...(r.product ? { product: r.product } : {}) });

export const fetchReportList = () => getData<ReportMeta[]>('/reports');
export const fetchReport = (name: string, range: RangeParams) => getData<ReportResult>(`/reports/${name}`, { params: rangeQuery(range) });
export const fetchDashboard = (range: RangeParams) => getData<DashboardSummary>('/reports/dashboard', { params: rangeQuery(range) });
export const saveDashboardWidgets = async (widgets: string[]): Promise<void> => {
  await http.put('/reports/dashboard/widgets', { widgets });
};
export const exportReport = (name: string, range: RangeParams, format: ExportFormat) =>
  downloadExport(`/reports/${name}/export`, { ...rangeQuery(range), format }, `${name}.${format}`);
export const exportTickets = (filters: TicketListFilters, format: ExportFormat) =>
  downloadExport('/tickets/export', { ...ticketFilterParams(filters), format }, `tickets.${format}`);

export const fetchSchedules = () => getData<ReportSchedule[]>('/reports/schedules');
export type ScheduleBody = Pick<ReportSchedule, 'reportName' | 'rangeDays' | 'format' | 'frequency' | 'hourUtc' | 'recipientIds'> & { dayOfWeek?: number; dayOfMonth?: number };
export const createScheduleRequest = (body: ScheduleBody) => sendData<ReportSchedule>('post', '/reports/schedules', body);
export const updateScheduleRequest = (id: string, body: Partial<ScheduleBody> & { isActive?: boolean }) => sendData<ReportSchedule>('patch', `/reports/schedules/${id}`, body);
export const deleteScheduleRequest = async (id: string): Promise<void> => {
  await http.delete(`/reports/schedules/${id}`);
};

/* ----- saved searches ----- */
export const fetchSavedSearches = (withCounts: boolean) => getData<SavedSearch[]>('/saved-searches', { params: withCounts ? { counts: 'true' } : {} });
export const createSavedSearchRequest = (body: { name: string; filters: SavedSearchFilters; shared: boolean; pinned: boolean }) => sendData<SavedSearch>('post', '/saved-searches', body);
export const updateSavedSearchRequest = (id: string, body: Partial<{ name: string; shared: boolean; pinned: boolean }>) => sendData<SavedSearch>('patch', `/saved-searches/${id}`, body);
export const deleteSavedSearchRequest = async (id: string): Promise<void> => {
  await http.delete(`/saved-searches/${id}`);
};

/* ----- satisfaction ----- */
export const fetchSurvey = (ticketId: string) => getData<SurveyState>(`/tickets/${ticketId}/survey`);
export const submitSurveyRequest = (ticketId: string, body: { rating: number; nps?: number; comment?: string }) => sendData<unknown>('post', `/tickets/${ticketId}/survey`, body);

/* ----- auth ----- */
export const loginRequest = (body: { tenant: string; email: string; password: string; remember: boolean }) =>
  sendData<{ accessToken: string }>('post', '/auth/login', body);

export const logoutRequest = async (): Promise<void> => {
  await http.post('/auth/logout');
};

/* ----- users ----- */
export const fetchMe = () => getData<Me>('/users/me');
export const fetchAssignableUsers = () => getData<UserSummary[]>('/users/assignable');

/* ----- tickets ----- */
/** Ticket-list filters → API query string (shared by list, export and saved-search application). */
export function ticketFilterParams(filters: TicketListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.status?.length) params.status = filters.status.join(',');
  if (filters.priority?.length) params.priority = filters.priority.join(',');
  if (filters.severity?.length) params.severity = filters.severity.join(',');
  if (filters.assigneeId) params.assigneeId = filters.assigneeId;
  else if (filters.unassigned) params.unassigned = 'true';
  if (filters.categoryId) params.categoryId = filters.categoryId;
  if (filters.product?.trim()) params.product = filters.product.trim();
  if (filters.q?.trim()) params.q = filters.q.trim();
  return params;
}

export async function fetchTickets(filters: TicketListFilters, cursor: string | null, signal?: AbortSignal): Promise<Page<Ticket>> {
  const params: Record<string, string> = { limit: '25', ...ticketFilterParams(filters) };
  if (cursor) params.cursor = cursor;
  const res = await http.get<Page<Ticket>>('/tickets', { params, ...(signal ? { signal } : {}) });
  return res.data;
}

export const fetchTicket = (id: string, signal?: AbortSignal) => getData<Ticket>(`/tickets/${id}`, signal ? { signal } : {});
export const createTicketRequest = (body: TicketInput & { submit: boolean }) => sendData<Ticket>('post', '/tickets', body);
export const updateTicketRequest = (id: string, body: Omit<Partial<TicketInput>, 'categoryId'> & { categoryId?: string | null; revision: number }) => sendData<Ticket>('patch', `/tickets/${id}`, body);
export const transitionTicketRequest = (id: string, body: { to: TicketStatus; revision: number; reason?: string }) =>
  sendData<Ticket>('post', `/tickets/${id}/transitions`, body);
export const assignTicketRequest = (id: string, body: { assigneeId: string; revision: number }) =>
  sendData<Ticket>('post', `/tickets/${id}/assign`, body);

/* ----- collaboration ----- */
export const fetchComments = (ticketId: string) => getData<Comment[]>(`/tickets/${ticketId}/comments`);
export const addCommentRequest = (ticketId: string, body: { body: string; visibility: CommentVisibility; parentId?: string }) =>
  sendData<Comment>('post', `/tickets/${ticketId}/comments`, body);
export const fetchTimeline = (ticketId: string) => getData<TimelineEvent[]>(`/tickets/${ticketId}/timeline`);

/* ----- notifications & preferences ----- */
export async function fetchNotifications(cursor: string | null, unreadOnly: boolean): Promise<Page<AppNotification>> {
  const res = await http.get<Page<AppNotification>>('/notifications', { params: { limit: '20', unread: String(unreadOnly), ...(cursor ? { cursor } : {}) } });
  return res.data;
}
export const fetchUnreadCount = () => getData<{ count: number }>('/notifications/unread-count');
export const markNotificationsRead = async (ids: string[]): Promise<void> => {
  await http.post('/notifications/read', { ids });
};
export const markAllNotificationsRead = async (): Promise<void> => {
  await http.post('/notifications/read-all');
};
export const updatePreferencesRequest = (prefs: Partial<NotificationPrefs>) => sendData<NotificationPrefs>('patch', '/users/me/preferences', prefs);

/* ----- attachments ----- */
export const fetchAttachments = (ticketId: string) => getData<Attachment[]>(`/tickets/${ticketId}/attachments`);

export async function uploadAttachmentsRequest(ticketId: string, files: File[], visibility: CommentVisibility, onProgress?: (pct: number) => void): Promise<Attachment[]> {
  const form = new FormData();
  form.append('visibility', visibility);
  for (const f of files) form.append('files', f);
  const res = await http.post<ApiEnvelope<Attachment[]>>(`/tickets/${ticketId}/attachments`, form, {
    timeout: 5 * 60_000,
    ...(onProgress ? { onUploadProgress: (e: { loaded: number; total?: number }) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0) } : {}),
  });
  return res.data.data;
}

/** The bearer token lives in memory, so downloads go through the authenticated client rather than a plain link. */
export async function downloadAttachment(ticketId: string, attachment: Pick<Attachment, '_id' | 'fileName'>): Promise<void> {
  const res = await http.get<Blob>(`/tickets/${ticketId}/attachments/${attachment._id}/download`, { responseType: 'blob', timeout: 5 * 60_000 });
  const url = URL.createObjectURL(res.data);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

export const deleteAttachmentRequest = async (ticketId: string, attachmentId: string): Promise<void> => {
  await http.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
};

/* ----- administration ----- */
export async function fetchUsers(params: { q?: string; role?: Role; page: number }): Promise<UserPage> {
  const res = await http.get<UserPage>('/users', { params: { pageSize: '25', page: String(params.page), ...(params.q ? { q: params.q } : {}), ...(params.role ? { role: params.role } : {}) } });
  return res.data;
}
export async function createUserRequest(body: { email: string; name: string; role: Role; organizationId?: string; customRoleId?: string }): Promise<{ user: AdminUser; inviteSent: boolean }> {
  const res = await http.post<{ data: AdminUser; meta: { inviteSent: boolean } }>('/users', body);
  return { user: res.data.data, inviteSent: res.data.meta.inviteSent };
}
export const updateUserRequest = (id: string, body: { name?: string; role?: Role; customRoleId?: string | null; isActive?: boolean; organizationId?: string | null }) =>
  sendData<AdminUser>('patch', `/users/${id}`, body);
export const resendInviteRequest = async (id: string): Promise<void> => {
  await http.post(`/users/${id}/invite`);
};
export const fetchOrganizations = () => getData<Organization[]>('/organizations');
export const createOrganizationRequest = (body: { name: string; tier: CustomerTier }) => sendData<Organization>('post', '/organizations', body);
export const updateOrganizationRequest = (id: string, body: Partial<Pick<Organization, 'name' | 'tier' | 'isActive'>>) =>
  sendData<Organization>('patch', `/organizations/${id}`, body);
export const fetchTenantSettings = () => getData<TenantSettings>('/tenant/settings');
export const updateTenantSettingsRequest = (body: { slaPolicy?: SlaPolicy; autoEscalateOnBreach?: boolean }) => sendData<TenantSettings>('patch', '/tenant/settings', body);
export async function fetchAudit(params: { entityType?: string; cursor: string | null }): Promise<Page<AuditEntry>> {
  const res = await http.get<Page<AuditEntry>>('/audit', { params: { limit: '50', ...(params.entityType ? { entityType: params.entityType } : {}), ...(params.cursor ? { cursor: params.cursor } : {}) } });
  return res.data;
}

/* ----- password flows ----- */
export const forgotPasswordRequest = async (body: { tenant: string; email: string }): Promise<void> => {
  await http.post('/auth/password/forgot', body);
};
export const changePasswordRequest = (body: { currentPassword: string; newPassword: string }) =>
  sendData<{ accessToken: string }>('post', '/auth/password/change', body);
export const resetPasswordRequest = async (body: { token: string; password: string }): Promise<void> => {
  await http.post('/auth/password/reset', body);
};

/* ----- branding ----- */
export const fetchPublicBranding = (slug: string) => getData<Branding>(`/branding/${encodeURIComponent(slug)}`);
export const fetchBranding = () => getData<Branding>('/tenant/branding');
export const updateBrandingRequest = (body: Partial<Record<'productName' | 'primaryColor' | 'loginMessage' | 'supportEmail', string | null>>) =>
  sendData<Branding>('patch', '/tenant/branding', body);
export async function uploadLogoRequest(file: File): Promise<Branding> {
  const form = new FormData();
  form.append('logo', file);
  const res = await http.post<ApiEnvelope<Branding>>('/tenant/branding/logo', form);
  return res.data.data;
}
export const deleteLogoRequest = async (): Promise<Branding> => (await http.delete<ApiEnvelope<Branding>>('/tenant/branding/logo')).data.data;

/* ----- privacy (GDPR) ----- */
export const fetchPrivacyRequests = (status?: PrivacyStatus) => getData<PrivacyRequest[]>('/privacy/requests', { params: status ? { status } : {} });
export const createPrivacyRequest = (body: { type: PrivacyType; subjectId?: string; reason?: string; redactContent?: boolean }) =>
  sendData<PrivacyRequest>('post', '/privacy/requests', body);
export const decidePrivacyRequest = (id: string, decision: 'approve' | 'reject', note?: string) =>
  sendData<PrivacyRequest>('post', `/privacy/requests/${id}/${decision}`, note ? { note } : {});
export const downloadPrivacyExport = (id: string) => downloadExport(`/privacy/requests/${id}/download`, {}, 'personal-data.zip');

/* ----- roles ----- */
export const fetchRoles = () => getData<RoleView[]>('/roles');
export const fetchPermissionCatalog = () => getData<PermissionCatalog>('/roles/permissions');
export const createRoleRequest = (body: { name: string; description?: string; baseRole: Role; permissions: string[] }) => sendData<RoleView>('post', '/roles', body);
export const updateRoleRequest = (id: string, body: { name?: string; description?: string | null; permissions?: string[] }) => sendData<RoleView>('patch', `/roles/${id}`, body);
export const deleteRoleRequest = async (id: string): Promise<void> => {
  await http.delete(`/roles/${id}`);
};

/* ----- ticket categories ----- */
export const fetchCategories = (includeInactive = false) => getData<TicketCategory[]>('/categories', { params: includeInactive ? { includeInactive: 'true' } : {} });
export const createCategoryRequest = (body: { name: string; description?: string; sortOrder?: number }) => sendData<TicketCategory>('post', '/categories', body);
export const updateCategoryRequest = (id: string, body: { name?: string; description?: string | null; isActive?: boolean; sortOrder?: number }) =>
  sendData<TicketCategory>('patch', `/categories/${id}`, body);
export const deleteCategoryRequest = async (id: string): Promise<void> => {
  await http.delete(`/categories/${id}`);
};

/* ----- limits ----- */
export const fetchLimits = () => getData<UploadLimits>('/meta/limits');

/* ----- personal UI preferences ----- */
export const updateUiPrefsRequest = (body: { locale?: 'en' | 'es'; theme?: 'system' | 'light' | 'dark'; accent?: string }) =>
  sendData<{ locale: 'en' | 'es'; theme: 'system' | 'light' | 'dark'; accent: string }>('patch', '/users/me/ui', body);
